import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
// ⚠️ Sous-chemin `/client` OBLIGATOIRE : la racine `@orgazone/auth-web` est le plugin Fastify (argon2,
// fastify-plugin) — l'importer ici ferait entrer du code serveur dans le bundle navigateur.
import { resolveSession, type AuthMode } from '@orgazone/auth-web/client';
import { markSessionEstablished } from '@orgazone/ui-web';
import { api, type CrossTenantContext, type SessionUser } from './api';
import { setCurrentVocabularyLang } from './vocabulary';
import { browserLang, currentLang } from './lang';
import { loadCatalogue } from './i18n';

/**
 * LA SESSION DU BACK-OFFICE, côté navigateur.
 *
 * ⚠️ La RÈGLE de résolution ne vit pas ici : elle est dans `@orgazone/auth-web/client`, identique aux
 * quatre surfaces web et testée là-bas. Elle distingue trois situations que le code d'avant confondait
 * en un seul `catch` — « pas de session » (401, cas NORMAL), « mode ouvert », et « je n'ai pas pu
 * savoir » (service injoignable). Confondre les deux dernières affichait « non connecté » devant un
 * service en panne, et l'utilisateur retapait ses identifiants pour rien (§23.0, échec avalé).
 */
interface AuthCtx {
  /** La session a été résolue au moins une fois (évite un flash d'écran de connexion). */
  ready: boolean;
  mode: AuthMode;
  /** Panne du service d'authentification, message RÉEL — jamais transformé en « déconnecté ». */
  error: string | null;
  user: SessionUser | null;
  /** Détient-elle ce droit ? Comparaison EXACTE : le RBAC du serveur ne pose aucun joker (cf. api.ts). */
  can: (permission: string) => boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [mode, setMode] = useState<AuthMode>({ oidc: false, armed: true });
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<SessionUser | null>(null);

  const refresh = useCallback(async () => {
    const s = await resolveSession<SessionUser>({
      fetchMode: () => api.authMode(),
      fetchUser: async () => (await api.me()).user,
    });
    setMode(s.mode);
    // ⚠️ LA LANGUE AVANT L'ÉTAT : elle décide des mots que le premier rendu emploiera, et la poser
    //    après ferait afficher « Saison » puis « Temporada » pour le même champ (§23.0). Même
    //    discipline que le vocabulaire du magasin, posé avant que le contexte ne soit commité.
    // ⚠️ SANS SESSION, C'EST LE NAVIGATEUR QUI DÉCIDE (décision utilisateur) : sur l'écran
    //    de connexion il n'y a pas encore de compte à interroger, et la porte d'entrée d'un back-office
    //    entièrement traduit parlait français à tout le monde.
    // ⚠️ UNE FOIS CONNECTÉ, LE COMPTE DÉCIDE — Y COMPRIS QUAND IL N'A RIEN CHOISI : un compte sans
    //    langue retombe sur la plateforme, comme avant. Le navigateur ne sert QUE l'anonyme. Élargir
    //    aux comptes sans langue changerait ce que des utilisateurs en service voient déjà ; c'est une
    //    décision à part, pas un effet de bord de celle-ci.
    setCurrentVocabularyLang(s.user ? s.user.locale : browserLang());
    // ⚠️ LE CATALOGUE DE CETTE LANGUE, ET LUI SEUL — attendu AVANT le premier rendu : chargé après,
    //    l'écran afficherait le français puis se corrigerait sous les yeux de l'utilisateur (§23.0).
    //    Un compte français n'attend rien (la clé EST le texte) ; un échec laisse l'interface en
    //    français plutôt que de refuser d'ouvrir l'application.
    await loadCatalogue(currentLang());
    setUser(s.user);
    setError(s.error);
    setReady(true);
    // ⚠️ Marque la session comme ÉTABLIE pour la politique 401 partagée : sans cela, un 401 reçu avant
    // toute connexion déclencherait un rebond, et l'écran de connexion se rechargerait en boucle.
    if (s.user) markSessionEstablished();
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const can = useCallback((permission: string) => user?.permissions.includes(permission) ?? false, [user]);

  const value = useMemo<AuthCtx>(() => ({ ready, mode, error, user, can, refresh }), [ready, mode, error, user, can, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth hors AuthProvider');
  return v;
}

/**
 * ⚠️ `useAuthorizedStores` A ÉTÉ RETIRÉ (tranche Articles). Les magasins ouverts arrivent désormais avec
 * le magasin ACTIF et le droit d'en changer, en une seule lecture (`/admin/store-context`, cf.
 * `store-context.tsx`) : deux façons d'obtenir la même liste finissent par en donner deux différentes,
 * et le bandeau afficherait alors un périmètre qui n'est pas celui que l'API applique.
 */

/**
 * L'ACCÈS CROSS-TENANT de la session courante (doctrine §4ter).
 *
 * ⚠️ TROIS ÉTATS, comme pour les magasins : en cours de lecture, échec avec son motif RÉEL, et réponse.
 * Un échec ne doit surtout pas se replier sur « pas de cross-tenant » : ce repli-là ferait DISPARAÎTRE
 * le bandeau au moment précis où il compte, et l'agent croirait être chez lui (§23.0, échec avalé).
 */
export function useCrossTenant(): { loading: boolean; error: string | null; ctx: CrossTenantContext | null } {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ctx, setCtx] = useState<CrossTenantContext | null>(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let vivant = true;
    setLoading(true);
    api.crossTenant()
      .then((r) => { if (vivant) { setCtx(r); setError(null); } })
      .catch((e: unknown) => { if (vivant) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (vivant) setLoading(false); });
    return () => { vivant = false; };
  }, [user]);

  return { loading, error, ctx };
}
